<script setup lang="ts">
import QRCode from 'qrcode';
import liff from '@line/liff';
// META
useHead({
    title: 'สั่งซื้อกับสมศรี',
});

const deeplinkId = useRoute().params.deeplinkId.toString();

// SERVICE
const deeplinkService = useDeeplink();

// ELEMENT
const qrcodeCanvas = ref<HTMLCanvasElement>();

// VARIABLE
const { isMobile } = useDevice();
const { LIFF_ID } = useRuntimeConfig().public;
const blobs = [
    { size: '21.25rem', top: '-7.5rem', left: '-6.25rem', opacity: 0.1 },
    { size: '16.25rem', bottom: '-5.625rem', right: '-5rem', opacity: 0.08 },
    { size: '10rem', top: '55%', left: '-3.75rem', opacity: 0.06 },
    { size: '8.75rem', top: '8%', right: '8%', opacity: 0.07 },
    { size: '5.625rem', top: '18%', left: '12%', opacity: 0.09 },
    { size: '7.5rem', bottom: '12%', right: '18%', opacity: 0.06 },
    { size: '3.75rem', bottom: '20%', left: '20%', opacity: 0.1 },
];

// FUNCTION
async function getIsValidDeeplink() {
    try {
        const isValid = await deeplinkService.isValidDeeplink(deeplinkId);
        return isValid;
    } catch (err) {
        console.error(err);
        useBugsnag().notify(err as Error);
        return false;
    }
}

async function saveLog() {
    try {
        // เว็บต้นทางที่แปะลิงก์นี้ — ฝั่ง SSR อ่านจาก header, ฝั่ง client อ่านจาก document.referrer (ว่าง = ไม่ส่ง)
        const referer = import.meta.server ? useRequestHeaders(['referer']).referer : document.referrer;
        await deeplinkService.createVisitLog(deeplinkId, referer || undefined);
    } catch (err) {
        console.error(err);
        useBugsnag().notify(err as Error);
        throw createError({
            statusCode: 500,
            statusMessage: 'Internal Server Error',
        });
    }
}

function generateQrcode() {
    const canvas = qrcodeCanvas.value;
    QRCode.toCanvas(canvas, window.location.href, {
        scale: 7,
        margin: 0,
    });
}

async function main() {
    // INIT LIFF
    await liff.init({
        liffId: LIFF_ID,
    });

    // LOGIN LIFF
    if (!liff.isInClient() && isMobile) {
        window.location.href = `line://app/${liff.id}?deeplinkId=${deeplinkId}`;
        setTimeout(() => {
            liff.closeWindow();
        }, 3000);
        return;
    }
}

// SETUP SCRIPT
const isValidDeeplink = await getIsValidDeeplink();
if (!isValidDeeplink) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Not Found',
    });
}
await saveLog();

// LIFE CYCLE
onMounted(async () => {
    await nextTick();
    generateQrcode();
    main();
});
</script>

<template>
  <!-- page-root -->
  <div
    class="
      min-h-screen flex items-center justify-center
      relative overflow-hidden
      bg-[linear-gradient(160deg,_#06C755_0%,_#05b34c_45%,_#049143_100%)]
    "
  >
    <!-- Background decorations: dot grid -->
    <div
      class="
        fixed inset-0 z-0
        [background-image:radial-gradient(rgba(255,255,255,0.16)_0.1rem,transparent_0.1rem)]
        [background-size:1.625rem_1.625rem]
      "
    />

    <!-- Blobs -->
    <div
      v-for="(blob, i) in blobs"
      :key="i"
      class="fixed z-0 rounded-full"
      :style="{
        width: blob.size,
        height: blob.size,
        top: blob.top,
        bottom: blob.bottom,
        left: blob.left,
        right: blob.right,
        backgroundColor: `rgba(255, 255, 255, ${blob.opacity})`,
      }"
    />

    <!-- deeplink-wrapper -->
    <div
      class="
        relative z-[2] flex flex-col items-center justify-center
        text-center p-5 w-full max-w-sm
      "
    >
      <!-- Heading -->
      <div class="flex flex-col items-center leading-none mb-2.5">
        <span
          class="
            text-lg font-medium tracking-[0.25rem]
            text-white/90 [text-shadow:0_2px_10px_rgba(0,0,0,0.12)]
            mb-0.5
          "
        >เพิ่มเพื่อน</span>
        <span
          class="
            text-6xl font-extrabold tracking-normal
            text-white [text-shadow:0_6px_24px_rgba(0,50,25,0.25)]
          "
        >LINE</span>
      </div>

      <div
        class="
          text-xs font-medium text-white/90
          tracking-[0.1875rem] mb-7
        "
      >
        SCAN QR CODE TO ADD FRIEND
      </div>

      <!-- Card -->
      <div
        class="
          relative w-full bg-white rounded-3xl
          px-6 pt-8 pb-7
          shadow-2xl
        "
      >
        <!-- QR Box -->
        <div
          class="
            relative inline-block p-4 bg-white
            border-2 border-[#e9f9ef] rounded-2xl
            shadow-xs
          "
        >
          <canvas
            ref="qrcodeCanvas"
            class="block rounded-sm w-48 h-48"
          />
          <!-- LINE Logo Overlay -->
          <div
            class="
              absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              w-10 h-10 bg-white rounded-full
              flex items-center justify-center p-px
              shadow-sm
            "
          >
            <img
              src="/line-logo.svg"
              alt="LINE"
              class="w-full h-full object-cover rounded-full block"
            >
          </div>
        </div>

        <!-- Divider -->
        <div class="w-12 h-0.5 bg-green-500 rounded-sm mx-auto mt-5 mb-4" />

        <p class="text-base font-semibold text-gray-900 leading-[1.8]">
          สแกนแล้วมาสั่งซื้อสินค้ากับเราเลย!
        </p>
        <p class="text-xs font-normal text-gray-400 mt-1.5">
          รับข่าวสารล่าสุดและข้อมูลพิเศษ
        </p>
      </div>

      <p class="mt-5 text-xs tracking-[0.125rem] text-white/85 uppercase">
        สมศรีมีเสื้อ · SOMSRITSHIRT
      </p>
    </div>
  </div>
</template>